import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePortal } from '../../context/PortalContext';
import { getVisitAnalytics, getVenueVisits } from '../../services/visitService';
import VenueEmptyState from '../../components/EmptyState/VenueEmptyState';
import VisitEmptyState from '../../components/EmptyState/VisitEmptyState';
import SectionHeader from '../../components/SectionHeader/SectionHeader';
import './DeveloperPortalLayout.css';

interface DashboardProps {
  onNavigate: (section: string) => void;
}

interface VisitStats {
  total_visits: number;
  physical_visits: number;
  online_visits: number;
  active_visits: number;
  active_physical_visits: number;
  active_online_visits: number;
  time_range: '24h' | 'today' | 'lifetime';
  time_range_label: string;
}

interface VenueStats {
  total_venues: number;
  general_visits: number;
  location_specific_visits: number;
  online_visits: number;
}

type TimeRange = '24h' | 'today' | 'lifetime';

const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const { venues, loadVenues } = usePortal();
  const [visitStats, setVisitStats] = useState<VisitStats | null>(null);
  const [venueStats, setVenueStats] = useState<VenueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');

  useEffect(() => {
    loadVenues();
  }, []);

  useEffect(() => {
    if (venues.length > 0) {
      loadDashboardData();
    }
  }, [venues, timeRange]); // Reload when time range changes

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load visit analytics for all venues with selected time range
        const allVenueStats = await Promise.all(
          venues.map(async (venue) => {
            try {
              const analytics = await getVisitAnalytics(venue.id, 30, timeRange);
              const visits = await getVenueVisits(venue.id, 1, 1000);
              return { venue, analytics, visits: visits.items };
            } catch (err) {
              console.error(`Error loading data for venue ${venue.id}:`, err);
              return { venue, analytics: null, visits: [] };
            }
          })
        );

        // Aggregate stats across all venues
        const totalVisits = allVenueStats.reduce((sum, { analytics }) => 
          sum + (analytics?.total_visits || 0), 0);
        const totalPhysicalVisits = allVenueStats.reduce((sum, { analytics }) => 
          sum + (analytics?.physical_visits || 0), 0);
        const totalOnlineVisits = allVenueStats.reduce((sum, { analytics }) => 
          sum + (analytics?.online_visits || 0), 0);
        const totalActiveVisits = allVenueStats.reduce((sum, { analytics }) => 
          sum + (analytics?.active_visits || 0), 0);
        const totalActivePhysicalVisits = allVenueStats.reduce((sum, { analytics }) => 
          sum + (analytics?.active_physical_visits || 0), 0);
        const totalActiveOnlineVisits = allVenueStats.reduce((sum, { analytics }) => 
          sum + (analytics?.active_online_visits || 0), 0);

        setVisitStats({
          total_visits: totalVisits,
          physical_visits: totalPhysicalVisits,
          online_visits: totalOnlineVisits,
          active_visits: totalActiveVisits,
          active_physical_visits: totalActivePhysicalVisits,
          active_online_visits: totalActiveOnlineVisits,
          time_range: timeRange,
          time_range_label: {
            '24h': 'Last 24 Hours',
            'today': 'Today',
            'lifetime': 'All Time'
          }[timeRange]
        });

        // Calculate venue-specific stats
        const generalVisits = allVenueStats.reduce((sum, { visits }) => 
          sum + visits.filter(v => v.visit_type === 'GENERAL').length, 0);
        const locationSpecificVisits = allVenueStats.reduce((sum, { visits }) => 
          sum + visits.filter(v => v.visit_type === 'LOCATION_SPECIFIC').length, 0);
        const onlineVisits = allVenueStats.reduce((sum, { visits }) => 
          sum + visits.filter(v => v.visit_type === 'ONLINE').length, 0);

        setVenueStats({
          total_venues: venues.length,
          general_visits: generalVisits,
          location_specific_visits: locationSpecificVisits,
          online_visits: onlineVisits
        });
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, color = 'primary' }: {
    title: string;
    value: string | number;
    subtitle?: string;
    color?: 'primary' | 'success' | 'warning' | 'info';
  }) => (
    <div className={`stat-card stat-card-${color}`}>
      <div className="stat-card-header">
        <h3>{title}</h3>
      </div>
      <div className="stat-value">{value}</div>
      {subtitle && <div className="stat-subtitle">{subtitle}</div>}
    </div>
  );

  const QuickLink = ({ title, onClick }: {
    title: string;
    onClick: () => void;
  }) => (
    <button className="quick-link" onClick={onClick}>
      {title}
    </button>
  );

  if (error) {
    return (
      <div className="container">
        <SectionHeader
          title="Dashboard"
          description="Overview of your PlatefullAI business metrics."
        />
        <div className="error">{error}</div>
      </div>
    );
  }

  // Show venue empty state if no venues
  if (venues.length === 0) {
    return (
      <div className="container">
        <SectionHeader
          title="Dashboard"
          description="Overview of your PlatefullAI business metrics."
        />
        <VenueEmptyState onAddVenue={() => onNavigate('venues')} />
      </div>
    );
  }

  // Show visit empty state if no visits (only check for lifetime)
  if (visitStats && timeRange === 'lifetime' && visitStats.total_visits === 0) {
    return (
      <div className="container">
        <SectionHeader
          title="Dashboard"
          description="Overview of your PlatefullAI business metrics."
        />
        <VisitEmptyState onViewVenues={() => onNavigate('venues')} />
      </div>
    );
  }

  return (
    <div className="container">
      <SectionHeader
        title="Dashboard"
        description="Overview of your PlatefullAI business metrics."
      />

      {loading ? (
        <div className="loading">Loading dashboard data...</div>
      ) : (
        <>
          {/* Quick Actions */}
          <div className="section">
        <h4>Quick Actions</h4>
        <div className="quick-actions">
          <QuickLink
            title="Data Explorer"
            onClick={() => onNavigate('data-explorer')}
          />
          <QuickLink
            title="Charts & Widgets"
            onClick={() => onNavigate('charts')}
          />
          <QuickLink
            title="Manage Venues"
            onClick={() => onNavigate('venues')}
          />
          <QuickLink
            title="My Applications"
            onClick={() => onNavigate('applications')}
          />
        </div>
      </div>

      {/* Visit Statistics with Time Range Selector */}
      <div className="section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h4>Visit Analytics</h4>
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value as TimeRange)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid var(--color-border-primary)',
              fontSize: '14px',
              cursor: 'pointer',
              backgroundColor: 'var(--color-bg-card)',
              color: 'var(--color-text-primary)',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--color-border-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--color-border-primary)'}
          >
            <option value="24h">Last 24 Hours</option>
            <option value="today">Today</option>
            <option value="lifetime">All Time</option>
          </select>
        </div>
        <div className="stats-grid">
          <StatCard
            title="Total Visits"
            value={visitStats?.total_visits || 0}
            subtitle={visitStats?.time_range_label || ''}
            color="primary"
          />
          <StatCard
            title="Physical Visits"
            value={visitStats?.physical_visits || 0}
            subtitle="QR code check-ins"
            color="success"
          />
          <StatCard
            title="Online Visits"
            value={visitStats?.online_visits || 0}
            subtitle="Digital interactions"
            color="info"
          />
          <StatCard
            title="Active Now"
            value={visitStats?.active_visits || 0}
            subtitle="Currently active"
            color="warning"
          />
        </div>
      </div>

      {/* Venue Statistics */}
      <div className="section">
        <h4>Venue Statistics</h4>
        <div className="stats-grid">
          <StatCard
            title="Total Venues"
            value={venueStats?.total_venues || 0}
            subtitle="Registered venues"
            color="primary"
          />
          <StatCard
            title="General Visits"
            value={venueStats?.general_visits || 0}
            subtitle="Venue-level check-ins"
            color="success"
          />
          <StatCard
            title="Location Visits"
            value={venueStats?.location_specific_visits || 0}
            subtitle="Specific location check-ins"
            color="warning"
          />
          <StatCard
            title="Online Visits"
            value={venueStats?.online_visits || 0}
            subtitle="Digital interactions"
            color="info"
          />
        </div>
      </div>

      {/* Recent Activity Preview */}
      <div className="section">
        <h4>Recent Activity</h4>
        <div className="activity-preview">
          <p>Visit the <strong>Data Explorer</strong> to see detailed user visit information, dietary preferences, and health concerns.</p>
          <p>Check out <strong>Charts & Widgets</strong> for visual analytics and preference distribution insights.</p>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;